import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';

import { useAuth } from './contexts/AuthContext';
import { useTrialCheck } from './hooks/useTrialCheck';
import { notificationService } from './services/notificationService';
import { webPushService } from './services/webPushService';
import { Capacitor } from '@capacitor/core';
import ErrorBoundary from './components/common/ErrorBoundary';
import { logMobileDebugInfo } from './utils/mobileDebug';

import Layout from './components/layout/Layout';
import PublicLayout from './layouts/PublicLayout';
import SignInPage from './components/auth/SignInPage';
import SignupPage from './components/auth/SignupPage';
import CreateBusinessPage from './components/auth/CreateBusinessPage';
import RequireFirebaseAuth from './components/auth/RequireFirebaseAuth';
import PricingPage from './components/pricing/PricingPage';
import TermsPage from './components/pricing/TermsPage';
import PrivacyPage from './components/pricing/PrivacyPage';
import PhoneInputWithCountry from './components/auth/PhoneInputWithCountry';
import WorkerInvite from './components/auth/WorkerInvite';
import Dashboard from './components/pages/Dashboard';
import Orders from './components/pages/Orders';
import OrderDetails from './components/pages/OrderDetails';
import Inventory from './components/pages/Inventory';
import Customers from './components/pages/Customers';
import CustomerView from './components/pages/CustomerView';
import Payments from './components/pages/Payments';
import Profile from './components/pages/Profile';
import Settings from './components/pages/Settings';
import Subscription from './components/pages/Subscription';
import SupportChat from './components/pages/SupportChat';
import TodayReport from './components/pages/TodayReport';
import UserManagement from './components/pages/UserManagement';
import Features from './components/pages/Features';
import BluetoothPrinter from './components/pages/BluetoothPrinter';
import Notifications from './components/pages/Notifications';
import AIChatbot from './components/pages/AIChatbot';
import PublicMenu from './components/public/PublicMenu';
import TrialExpiredModal from './components/TrialExpiredModal';
import TableView from './components/pages/TableView';
import PrivateRoute from './components/PrivateRoute';
import OrderHistory from './components/pages/OrderHistory';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { currentUser, loading, tenantData } = useAuth();
  const { isTrialExpired } = useTrialCheck();
  const isProvisioned = Boolean(tenantData);

  React.useEffect(() => {
    logMobileDebugInfo('App Start', { currentUser: !!currentUser, loading });
  }, [currentUser, loading]);

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          await notificationService.initialize();
        }
        if (!Capacitor.isNativePlatform()) {
          await webPushService.initialize();
        }
      } catch (error) {
        console.error('Error initializing notifications:', error);
      }
    };

    if (currentUser) {
      initializeNotifications();
    }
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        {/* Public menu — no layout */}
        <Route path="/menu/:tenantId" element={<PublicMenu />} />
        <Route path="/menu/:tenantId/table/:tableNumber" element={<PublicMenu />} />

        {/* Public layout group */}
        <Route element={<PublicLayout />}>
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/signup"
            element={
              currentUser ? (
                isProvisioned ? <Navigate to="/dashboard" replace /> : <Navigate to="/pricing" replace />
              ) : (
                <SignupPage />
              )
            }
          />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/login" element={<Navigate to="/signin" replace />} />
          <Route
            path="/create-business"
            element={
              <RequireFirebaseAuth>
                {isProvisioned ? <Navigate to="/dashboard" replace /> : <CreateBusinessPage />}
              </RequireFirebaseAuth>
            }
          />
          <Route path="/onboarding" element={<Navigate to="/create-business" replace />} />
          <Route
            path="/worker-invite"
            element={!currentUser ? <WorkerInvite /> : <Navigate to="/dashboard" replace />}
          />
          <Route path="/phone-input" element={!currentUser ? <PhoneInputWithCountry value="" onChange={() => {}} /> : <Navigate to="/dashboard" />} />
        </Route>

        <Route path="/subscription" element={<PrivateRoute><Subscription /></PrivateRoute>} />

        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:orderId" element={<OrderDetails />} />
          <Route path="order-history" element={<OrderHistory />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:customerId" element={<CustomerView />} />
          <Route path="payments" element={<Payments />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="support-chat" element={<SupportChat />} />
          <Route path="today-report" element={<TodayReport />} />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="features" element={<Features />} />
          <Route path="table-view" element={<TableView />} />
          <Route path="bluetooth-printer" element={<BluetoothPrinter />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="ai-chatbot" element={<AIChatbot />} />
        </Route>
      </Routes>

      {isTrialExpired && <TrialExpiredModal />}
    </div>
  );
}

export default App;
